function ordinalSuffixOf(i: number) {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return `${i}st`;
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }
  return `${i}th`;
}

function checkMask(str: string, mask?: string, max = 14, alternative = false, maskMessage = '') {
  if (!str) return { isOk: true, message: [''], lastOk: 0 };
  let isOk = true;
  let message = '';
  let lastOk = 0;

  if (!mask) {
    if (str.length > max) message = `Must not exceed ${max} characters`;
    if (str.length < 5) message = 'Must be larger than 5 characters';
    return {
      isOk: !message,
      message: [message],
      severity: !message ? undefined : 'error',
      lastOk,
    };
  }

  const charsQty = mask[mask.length - 1] === ']' ? mask.length - 1 : mask.length;
  const maxQty = mask[mask.length - 1] === ']' ? mask.length - 1 : 256; // no max
  const minQty = mask[mask.length - 1] === ']' ? mask.length - 1 : mask.length;

  // we want the for loop in order to loop through both the str and mask at once
  // eslint-disable-next-line unicorn/no-for-loop
  for (let i = 0; i < str.length; i += 1) {
    if (i === charsQty) break;
    if (!['@', '#', '&', '*'].includes(mask[i]) && str[i] !== mask[i]) {
      isOk = false;
      message = alternative
        ? `or for the alternative mask "${mask[i]}"`
        : `${ordinalSuffixOf(i + 1)} character must be "${mask[i]}"`;
    } // is different
    switch (mask[i]) {
      case '@':
        if (!/^[A-Za-z]$/.test(str[i])) {
          isOk = false;
          if (alternative && !maskMessage.includes(`${ordinalSuffixOf(i + 1)} character must be a letter`)) {
            message = 'or for the alternative mask a letter';
          } else if (!alternative) message = `${ordinalSuffixOf(i + 1)} character must be a letter`;
        } // must be an alphabetic character
        break;
      case '#':
        if (!/^[0-9]$/.test(str[i])) {
          isOk = false;
          if (alternative && !maskMessage.includes(`${ordinalSuffixOf(i + 1)} character must be a number`)) {
            message = 'or for the alternative mask a number';
          } else if (!alternative) message = `${ordinalSuffixOf(i + 1)} character must be a number`;
        } // must be a numeric character
        break;
      case '&':
        if (!/^[0-9A-Za-z]$/.test(str[i])) {
          isOk = false;
          if (
            alternative &&
            !maskMessage.includes(`${ordinalSuffixOf(i + 1)} character must be a letter or a number`)
          ) {
            message = 'or for the alternative mask a number';
          } else if (!alternative) message = `${ordinalSuffixOf(i + 1)} character must be a letter or a number`;
        } // must be an alphabetic or a numeric character
        break;
      case '*':
        if (!/[^ ]$/.test(str[i])) {
          isOk = false;
          if (alternative && !maskMessage.includes(`${ordinalSuffixOf(i + 1)} char can not be a space`)) {
            message = 'or for the alternative mask not be a space';
          } else if (!alternative) message = `${ordinalSuffixOf(i + 1)} char can not be a space`;
        } // must be any character except for a space
        break;
      default:
        // do nothing
        break;
    }
    if (isOk) lastOk += 1;
  }

  if (str.length > maxQty) {
    return {
      isOk: false,
      message: [
        alternative
          ? `or for the alternative mask not exceed ${maxQty} characters`
          : `Must not exceed ${maxQty} characters`,
      ],
      severity: 'error',
      lastOk,
    };
  } // is larger

  if (str.length < minQty && isOk) {
    return {
      isOk: false,
      message: [
        alternative
          ? `or for the alternative mask must be at least' ${minQty} characters`
          : `must be at least' ${minQty} characters`,
      ],
      severity: 'warning',
      lastOk,
    };
  } // is shorter

  return {
    isOk,
    message: [message],
    severity: isOk ? undefined : 'error',
    lastOk,
  };
}

export function checkMasks(params: { str: string; mask?: string; maskAlt?: string; max?: number; name?: string }) {
  const { str, max = 14, mask, maskAlt } = params;

  const maskResult = checkMask(str, mask, max);
  const altMaskResult = maskAlt ? checkMask(str, maskAlt, max) : null;

  if (maskResult?.isOk) return maskResult;
  if (altMaskResult?.isOk) return altMaskResult;
  if (maskResult.severity === "error" && altMaskResult && altMaskResult.severity !== "error") return altMaskResult
  return maskResult;
}

export function checkPoIsValid(po: string, poMask: string, poMaskAlt?: string) {
  if (!po) return false;
  if (
    poMask &&
    checkMasks({
      str: po,
      mask: poMask,
      maskAlt: poMaskAlt,
      max: 15,
    }).severity
  )
    return false;
  if (!poMask && (po.length < 5 || po.length > 15)) return false;
  return true;
}

export function checkRnIsValid(rn: string, rnMask: string, rnMaskAlt?: string) {
  if (!rn) return false;
  if (rnMask && checkMasks({ str: rn, mask: rnMask, maskAlt: rnMaskAlt }).severity) return false;
  if (rn.length > 14) return false;
  return true;
}


export const checkFilledMasks = (
  data: {
    referenceNumber: string|null,
    rnMask?: string,
    rnAltMask?: string,
    mandatoryRN?: boolean,
    poNumber: string|null,
    poMask?: string,
    poAltMask?: string,
    mandatoryPO?: boolean,
}
  ) => {
    const {
      referenceNumber,
      rnMask,
      rnAltMask,
      mandatoryRN,
      poNumber,
      poMask,
      poAltMask,
      mandatoryPO
    } = data;
  if (
    mandatoryRN &&
    (!referenceNumber ||
      referenceNumber.length < 5 ||
      referenceNumber.length > 15 ||
      (rnMask &&
        checkMasks({
          str: referenceNumber,
          mask: rnMask,
          maskAlt: rnAltMask,
        }).severity)
      )
  )
    return false;
    if (
      mandatoryPO &&
      (!poNumber ||
        (!poMask && (poNumber.length < 5 || poNumber.length > 15)) ||
        (poMask &&
          checkMasks({
            str: poNumber,
            mask: poMask,
            maskAlt: poAltMask,
            max: 15,
          }).severity))
    )
    return false;
  return true;
};